import { useState, useEffect, useCallback, useMemo } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import * as Tooltip from '@radix-ui/react-tooltip'
import { Save, Loader2, Radio, CalendarClock, Archive, AlertTriangle, Eye, ChevronDown, ExternalLink, Lock } from 'lucide-react'
import { useParams, useNavigate, useBlocker } from 'react-router-dom'
import { toast } from 'sonner'
import { useBlockCanvas } from '../editor-shared/useBlockCanvas'
import { BlockPickerShelf } from '../editor-shared/BlockPickerShelf'
import { BlockEditorDndContext } from '../editor-shared/BlockEditorDndContext'
import { PAGE_BLOCK_REGISTRY } from '../page-blocks/registry'
import { PageCanvas } from './PageCanvas'
import { PagePreview } from './PagePreview'
import { PageRightShelf } from './PageRightShelf'
import {
  usePage,
  useUpdatePage,
  usePublishPage,
  useSchedulePage,
  useArchivePage,
} from '../../api/hooks'
import type { BlockContent, Page, PageStatus, PageBlockInput, ColumnBlock, PagePool } from '../../api/types'
import { COL_KEYS } from '../editor-shared/canvasTypes'
import type { ColKey, ColBlock } from '../editor-shared/canvasTypes'
import { usePreviewBroadcast } from '../../hooks/usePreviewBroadcast'
import { ReschedulePageDialog } from './ReschedulePageDialog'
// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<PageStatus, { label: string; className: string }> = {
  draft:     { label: 'Draft',     className: 'bg-fill text-fg-secondary border border-line' },
  scheduled: { label: 'Scheduled', className: 'bg-accent-primary-fill text-accent-primary-fg border border-accent-primary-solid' },
  live:      { label: 'Live',      className: 'bg-green-3 text-green-11 border border-green-7' },
  archived:  { label: 'Archived',  className: 'bg-fill text-fg-secondary border border-line opacity-50' },
}

function StatusBadge({ status }: { status: PageStatus }) {
  const { label, className } = STATUS_BADGE[status]
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${className}`}>
      {status === 'live' && <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-green-9 align-middle" />}
      {label}
    </span>
  )
}

// ─── Publish dialog ─────────────────────────────────────────────────────────

const PUBLISH_DESCRIPTION: Record<string, string> = {
  'front-page': 'This will replace the current live front page with this version.',
  'topic':      'This will replace the currently-live version of this topic page.',
  'region':     'This will replace the currently-live version of this region page.',
  'edition':    'This will archive the current live edition with this slug and make this version live.',
  'book':       'This will publish this book page.',
}

function PublishDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending,
  hasEmptyBlocks,
  pageKind,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  onConfirm: () => void
  isPending: boolean
  hasEmptyBlocks: boolean
  pageKind: string
}) {
  const [confirmText, setConfirmText] = useState('')

  useEffect(() => {
    if (!open) setConfirmText('')
  }, [open])

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-80 -translate-x-1/2 -translate-y-1/2 rounded border border-line bg-canvas p-5 shadow-xl focus:outline-none">
          <Dialog.Title className="mb-1 text-sm font-semibold text-fg">Publish Now</Dialog.Title>
          <Dialog.Description className="mb-4 text-xs text-fg-secondary">
            {PUBLISH_DESCRIPTION[pageKind] ?? 'This will publish this page.'}
          </Dialog.Description>
          {hasEmptyBlocks && (
            <div className="mb-4 rounded border border-warning-line bg-warning-fill p-3">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-warning-fg">
                <AlertTriangle size={12} aria-hidden="true" />
                This page has empty article blocks.
              </p>
            </div>
          )}
          <p className="mb-2 text-[11px] text-fg-secondary">Type <strong>confirm</strong> to proceed.</p>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="confirm"
            className="w-full rounded border border-line bg-canvas px-2 py-1.5 text-xs text-fg focus:border-accent-primary-solid focus:outline-none"
          />
          <div className="mt-4 flex justify-end gap-2">
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill"
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              disabled={confirmText !== 'confirm' || isPending}
              onClick={onConfirm}
              className="flex items-center gap-1.5 rounded border border-green-7 bg-green-3 px-3 py-1.5 text-xs font-semibold text-green-11 transition-colors hover:bg-green-4 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isPending && <Loader2 size={12} className="animate-spin" />}
              {isPending ? 'Publishing…' : 'Publish'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ─── Schedule dialog ──────────────────────────────────────────────────────────

function ScheduleDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending,
  requireConfirm,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  onConfirm: (dt: string) => void
  isPending: boolean
  requireConfirm: boolean
}) {
  const [value, setValue] = useState('')
  const [confirmText, setConfirmText] = useState('')
  const isValid = Boolean(value) && new Date(value) > new Date()

  useEffect(() => {
    if (!open) { setValue(''); setConfirmText('') }
  }, [open])

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-80 -translate-x-1/2 -translate-y-1/2 rounded border border-line bg-canvas p-5 shadow-xl focus:outline-none">
          <Dialog.Title className="mb-1 text-sm font-semibold text-fg">Schedule Publication</Dialog.Title>
          <Dialog.Description className="mb-4 text-xs text-fg-secondary">
            Choose a future date and time. The page will go live automatically.
          </Dialog.Description>
          <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
            Publish at
          </label>
          <div className="flex gap-1.5">
            <input
              type="datetime-local"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onBlur={() => {}}
              className="flex-1 rounded border border-line bg-canvas px-2 py-1.5 text-xs text-fg focus:border-accent-primary-solid focus:outline-none"
            />
            <button
              type="button"
              onClick={(e) => (e.currentTarget.previousElementSibling as HTMLInputElement)?.blur()}
              className="rounded border border-line bg-canvas px-2 py-1.5 text-xs font-semibold text-accent-primary-fg transition-colors hover:bg-fill"
            >
              OK
            </button>
          </div>
          {value && !isValid && (
            <p className="mt-1.5 flex items-center gap-1 text-[11px] text-error-fg">
              <AlertTriangle size={11} aria-hidden="true" />
              Must be a future date/time.
            </p>
          )}
          {requireConfirm && (
            <div className="mt-4 rounded border border-warning-line bg-warning-fill p-3">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-warning-fg">
                <AlertTriangle size={12} aria-hidden="true" />
                This page has empty article blocks.
              </p>
              <p className="mb-2 text-[11px] text-fg-secondary">Type <strong>confirm</strong> to schedule anyway.</p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="confirm"
                className="w-full rounded border border-line bg-canvas px-2 py-1.5 text-xs text-fg focus:border-accent-primary-solid focus:outline-none"
              />
            </div>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill"
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              disabled={!isValid || isPending || (requireConfirm && confirmText !== 'confirm')}
              onClick={() => {
                if (!isValid) return
                onConfirm(new Date(value).toISOString())
              }}
              className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isPending && <Loader2 size={12} className="animate-spin" />}
              Schedule
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ─── PageEditor ───────────────────────────────────────────────────────────────

export function PageEditor() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: page, isLoading, isError } = usePage(id ?? '')
  const { mutate: updatePage, isPending: isUpdating } = useUpdatePage(id ?? '')
  const { mutate: publishPage, isPending: isPublishing } = usePublishPage()
  const { mutate: schedulePage, isPending: isScheduling } = useSchedulePage()
  const { mutate: archivePage, isPending: isArchiving } = useArchivePage()

  const canvas = useBlockCanvas(() => setIsDirty(true))
  const [contentMap, setContentMap] = useState<Record<string, BlockContent>>({})
  const [pool, setPool] = useState<PagePool | undefined>(undefined)

  // Collect all article IDs currently placed anywhere on the page so the
  // left shelf can grey them out.
  const usedArticleIds = useMemo(() => {
    const ids = new Set<string>()
    for (const content of Object.values(contentMap)) {
      if (content?.source === 'static') {
        for (const id of content.articleIds ?? []) {
          if (id) ids.add(id)
        }
      }
    }
    return ids
  }, [contentMap])

  // True if any editorial slot (or column block) that requires articles has none assigned.
  const hasEmptyArticleBlocks = useMemo(() => {
    for (const slot of canvas.slots) {
      const def = PAGE_BLOCK_REGISTRY[slot.type]
      if (def?.category === 'editorial' && def.minArticles > 0) {
        const content = contentMap[slot.id]
        const count = content?.source === 'static' ? (content.articleIds?.filter(Boolean).length ?? 0) : 0
        if (count < def.minArticles) return true
      }
      // Check column blocks inside editorial-grid
      for (const colBlocks of Object.values(slot.columnBlocks ?? {})) {
        for (const colBlock of (colBlocks as ColBlock[])) {
          const colDef = PAGE_BLOCK_REGISTRY[colBlock.type as keyof typeof PAGE_BLOCK_REGISTRY]
          if (colDef?.category === 'editorial' && colDef.minArticles > 0) {
            const colKey = Object.keys(slot.columnBlocks ?? {}).find(k => (slot.columnBlocks?.[k as ColKey] as ColBlock[])?.some(b => b.id === colBlock.id))
            const contentKey = colKey ? `${slot.id}:${colKey}:${colBlock.id}` : undefined
            const content = contentKey ? contentMap[contentKey] : undefined
            const count = content?.source === 'static' ? (content.articleIds?.filter(Boolean).length ?? 0) : 0
            if (count < colDef.minArticles) return true
          }
        }
      }
    }
    return false
  }, [canvas.slots, contentMap])

  const [showPublish, setShowPublish] = useState(false)
  const [showSchedule, setShowSchedule] = useState(false)
  const [showReschedule, setShowReschedule] = useState(false)
  const [showPreview, setShowPreview] = useState(false)

  // Broadcast live state to any open popout preview window.
  usePreviewBroadcast(id ?? '', canvas.slots, contentMap, page?.title ?? '')

  function openPopoutPreview() {
    window.open(`/admin/pages/${id}/live-preview`, 'wire-live-preview', 'width=1440,height=900')
  }

  const isBusy = isUpdating || isPublishing || isScheduling || isArchiving

  const [isDirty, setIsDirty] = useState(false)

  // Block browser-level unload (refresh, close tab) when dirty
  useEffect(() => {
    if (!isDirty) return
    function handleBeforeUnload(e: BeforeUnloadEvent) { e.preventDefault() }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  // Block in-app navigation when dirty
  const blocker = useBlocker(useCallback(() => isDirty, [isDirty]))

  // Hydrate canvas + contentMap from loaded page.
  // Intentional one-time init from async data — not a reactive sync.
  // TODO: refactor to key-based reset when component is stable.
  useEffect(() => {
    if (!page) return
    canvas.setSlots(
      (page.blocks ?? [])
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((b) => {
          // Build columnBlocks from columnContent (new format) or config.column* (legacy/template)
          const columnBlocks: Partial<Record<ColKey, ColBlock[]>> = {}
          if (b.columnContent) {
            for (const [colKey, colBlockArr] of Object.entries(b.columnContent) as [ColKey, ColumnBlock[]][]) {
              if (!colBlockArr) continue
              columnBlocks[colKey] = colBlockArr.map((cb) => ({ id: cb.id, type: cb.type }))
            }
          } else {
            // Fallback: single block per column from config (layout-seeded pages)
            for (const colKey of COL_KEYS) {
              const t = b.config[colKey]
              if (t) columnBlocks[colKey] = [{ id: crypto.randomUUID(), type: t as string as typeof b.type }]
            }
          }
          return { id: b.id, type: b.type, order: b.order, config: b.config, columnBlocks }
        }),
    )
    const map: Record<string, BlockContent> = {}
    for (const b of page.blocks ?? []) {
      if (b.content) map[b.id] = b.content
      if (b.columnContent) {
        for (const [colKey, colBlockArr] of Object.entries(b.columnContent) as [ColKey, ColumnBlock[]][]) {
          if (!colBlockArr) continue
          for (const cb of colBlockArr) {
            if (cb.content) map[`${b.id}:${colKey}:${cb.id}`] = cb.content
          }
        }
      }
    }
    // For topic/region pages every block must use source:'pool'. Apply eagerly so the
    // preview and slot renderers don't wait for ContentSourceEditor to mount per slot.
    if (page.kind === 'topic' || page.kind === 'region') {
      for (const b of page.blocks ?? []) {
        if (map[b.id]?.source !== 'pool') {
          map[b.id] = { source: 'pool', articleIds: [] }
        }
      }
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setContentMap(map)
    // For topic/region pages the pool is always derived from the page slug — editors
    // don't configure it manually. Spread existing pool so maxResults/query are preserved.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (page.kind === 'topic') {
      setPool({ ...(page.pool ?? {}), topicSlug: page.slug })
    } else if (page.kind === 'region') {
      setPool({ ...(page.pool ?? {}), regionSlug: page.slug })
    } else {
      setPool(page.pool)
    }
  }, [page]) // eslint-disable-line react-hooks/exhaustive-deps

  function setBlockContent(slotId: string, content: BlockContent) {
    setIsDirty(true)
    setContentMap((prev) => ({ ...prev, [slotId]: content }))
  }

  function removeBlockContent(slotId: string) {
    setContentMap((prev) => {
      const next = { ...prev }
      delete next[slotId]
      for (const key of Object.keys(next)) {
        if (key.startsWith(`${slotId}:`)) delete next[key]
      }
      return next
    })
  }

  function buildInput(p: Page) {
    return {
      kind: p.kind,
      slug: p.slug,
      title: p.title,
      originLayoutId: p.originLayoutId,
      pool,
      ...(p.scheduledFor ? { scheduledFor: p.scheduledFor } : {}),
      blocks: canvas.slots.map((slot) => {
        const columnContent: Partial<Record<ColKey, ColumnBlock[]>> = {}
        for (const colKey of COL_KEYS) {
          const colBlocks = slot.columnBlocks?.[colKey] ?? []
          if (colBlocks.length > 0) {
            columnContent[colKey] = colBlocks.map((cb) => ({
              id: cb.id,
              type: cb.type,
              content: contentMap[`${slot.id}:${colKey}:${cb.id}`],
            }))
          }
        }
        return {
          id: slot.id,
          type: slot.type,
          order: slot.order,
          config: slot.config,
          content: contentMap[slot.id],
          ...(Object.keys(columnContent).length > 0 ? { columnContent } : {}),
        } satisfies PageBlockInput
      }),
    }
  }

  function handleSave() {
    if (!page) return
    updatePage(buildInput(page), {
      onSuccess: () => { setIsDirty(false); toast.success('Page saved.') },
      onError: () => toast.error('Failed to save page.'),
    })
  }

  function handlePublish() {
    if (!page) return
    publishPage(page.id, {
      onSuccess: () => toast.success('Page is now live.'),
      onError: () => toast.error('Failed to publish page.'),
    })
  }

  function handleSchedule(scheduledFor: string) {
    if (!page) return
    // Save current canvas content first, then schedule.
    updatePage(buildInput(page), {
      onSuccess: () => {
        schedulePage({ id: page.id, scheduledFor }, {
          onSuccess: () => {
            setIsDirty(false)
            setShowSchedule(false)
            toast.success('Page scheduled.')
          },
          onError: () => toast.error('Failed to schedule page.'),
        })
      },
      onError: () => toast.error('Failed to save page before scheduling.'),
    })
  }

  function handleReschedule(scheduledFor: string) {
    if (!page) return
    updatePage({ ...buildInput(page), scheduledFor }, {
      onSuccess: () => { setShowReschedule(false); toast.success('Page rescheduled.') },
      onError: () => toast.error('Failed to reschedule page.'),
    })
  }

  function handleArchive() {
    if (!page) return
    archivePage(page.id, {
      onSuccess: () => {
        toast.success('Page archived.')
        navigate('/admin/pages')
      },
      onError: () => toast.error('Failed to archive page.'),
    })
  }

  function handleArticleDrop(slotId: string, articleId: string) {
    // Resolve maxArticles for the target — handles both top-level slots and
    // 3-part column content keys (parentSlotId:colKey:colBlockId).
    const parts = slotId.split(':')
    let maxArticles = Infinity
    if (parts.length === 3) {
      const [parentId, colKey, colBlockId] = parts
      const parentSlot = canvas.slots.find((s) => s.id === parentId)
      const colBlock = parentSlot?.columnBlocks?.[colKey as import('../editor-shared/canvasTypes').ColKey]?.find((b) => b.id === colBlockId)
      if (colBlock) maxArticles = PAGE_BLOCK_REGISTRY[colBlock.type]?.maxArticles ?? Infinity
    } else {
      const slot = canvas.slots.find((s) => s.id === slotId)
      if (slot) maxArticles = PAGE_BLOCK_REGISTRY[slot.type]?.maxArticles ?? Infinity
    }

    const current = contentMap[slotId]
    const existingIds = current?.source === 'static' ? (current.articleIds ?? []) : []
    if (existingIds.includes(articleId)) return
    if (existingIds.length >= maxArticles) return

    if (current?.source === 'static') {
      setBlockContent(slotId, { ...current, articleIds: [...existingIds, articleId] })
    } else {
      setBlockContent(slotId, { source: 'static', articleIds: [articleId] })
    }
  }

  const readOnly = page?.status === 'archived' || page?.status === 'live'

  // ─── Error / loading states ────────────────────────────────────

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="flex max-w-sm flex-col items-center gap-3 rounded border border-error-line bg-error-fill px-6 py-5 text-center">
          <AlertTriangle size={20} className="text-error-fg" aria-hidden="true" />
          <p className="text-sm font-semibold text-error-fg">Failed to load page</p>
          <p className="text-xs text-error-fg opacity-80">The page could not be retrieved. It may not exist, or there may be a network issue.</p>
        </div>
      </div>
    )
  }

  if (isLoading || !page) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 size={20} className="animate-spin text-fg-secondary" />
      </div>
    )
  }

  // ─── Render ──────────────────────────────────────────────────────

  return (
    <BlockEditorDndContext canvas={canvas} onArticleDrop={handleArticleDrop}>
    <div className="flex overflow-hidden" style={{ height: 'calc(100vh - 3rem)' }}>

      {/* Unsaved-changes navigation guard */}
      <Dialog.Root open={blocker.state === 'blocked'} onOpenChange={() => blocker.reset?.()}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas p-6 shadow-xl outline-none"
            aria-describedby="nav-guard-desc"
          >
            <Dialog.Title className="mb-1 text-base font-semibold text-fg">
              Unsaved changes
            </Dialog.Title>
            <Dialog.Description id="nav-guard-desc" className="mb-6 text-sm text-fg-secondary">
              You have unsaved edits. If you leave now, your changes will be lost.
            </Dialog.Description>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => blocker.reset?.()}
                className="rounded border border-line bg-canvas px-4 py-2 text-sm font-semibold text-fg transition-colors hover:bg-fill"
              >
                Stay and keep editing
              </button>
              <button
                type="button"
                onClick={() => blocker.proceed?.()}
                className="rounded border border-red-600 bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700"
              >
                Discard and leave
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <PublishDialog
        open={showPublish}
        onOpenChange={setShowPublish}
        onConfirm={() => { setShowPublish(false); handlePublish() }}
        isPending={isPublishing}
        hasEmptyBlocks={hasEmptyArticleBlocks}
        pageKind={page.kind}
      />

      <ScheduleDialog
        open={showSchedule}
        onOpenChange={setShowSchedule}
        onConfirm={handleSchedule}
        isPending={isScheduling}
        requireConfirm={hasEmptyArticleBlocks}
      />

      <ReschedulePageDialog
        open={showReschedule}
        onOpenChange={setShowReschedule}
        onConfirm={handleReschedule}
        isPending={isUpdating}
        pageTitle={page.title}
        currentScheduledFor={page.scheduledFor}
      />

      {/* Page preview overlay */}
      <Dialog.Root open={showPreview} onOpenChange={setShowPreview}>
        <Dialog.Portal>
          <Dialog.Content
            className="fixed inset-0 z-50 flex flex-col bg-canvas outline-none"
            aria-describedby={undefined}
          >
            <Dialog.Title className="sr-only">Page Preview</Dialog.Title>
            <PagePreview
              slots={canvas.slots}
              contentMap={contentMap}
              pageTitle={page.title}
              pool={pool}
              pageKind={page.kind}
              pageSlug={page.slug}
            />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <BlockPickerShelf canvas={canvas} usedArticleIds={usedArticleIds} readOnly={readOnly} hideArticles={page.kind === 'topic' || page.kind === 'region'} />

      {/* Center — toolbar + canvas */}
      <section className="flex flex-1 flex-col overflow-hidden">

        {/* Toolbar */}
        <div className="flex items-center gap-3 border-b border-line bg-accent-tertiary-fill px-5 py-2">

          {/* Title + status */}
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <h1 className="truncate text-sm font-semibold text-fg">{page.title}</h1>
            <StatusBadge status={page.status} />
            {page.status === 'scheduled' && page.scheduledFor && (
              <span className="flex items-center gap-1 text-[11px] text-accent-primary-fg">
                <CalendarClock size={11} aria-hidden="true" />
                {new Date(page.scheduledFor).toLocaleString('en-US', {
                  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                })}
              </span>
            )}
          </div>

          {/* Preview split button */}
          <div className="flex items-stretch overflow-hidden rounded border border-line bg-canvas text-xs font-semibold text-fg">
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 transition-colors hover:bg-fill"
            >
              <Eye size={12} aria-hidden="true" />
              Preview
            </button>
            <div className="w-px self-stretch bg-line" aria-hidden="true" />
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className="flex items-center px-1.5 py-1.5 transition-colors hover:bg-fill"
                  aria-label="More preview options"
                >
                  <ChevronDown size={11} aria-hidden="true" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  sideOffset={6}
                  align="end"
                  className="z-50 min-w-48 rounded border border-line bg-canvas py-1 shadow-lg"
                >
                  <DropdownMenu.Item
                    onSelect={openPopoutPreview}
                    className="flex cursor-default items-center gap-2 px-3 py-1.5 text-xs text-fg outline-none hover:bg-fill focus:bg-fill"
                  >
                    <ExternalLink size={12} aria-hidden="true" />
                    Open in new window
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>

          {/* Actions */}
          {!readOnly && (
            <div className="flex shrink-0 items-center gap-2">
              {/* Save */}
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isBusy}
                    className={`flex items-center gap-1.5 rounded border px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${isDirty && !isBusy ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700' : 'border-line bg-canvas text-fg hover:bg-fill'}`}
                  >
                    {isUpdating
                      ? <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                      : <Save size={12} aria-hidden="true" />
                    }
                    {isUpdating ? 'Saving…' : 'Save'}
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content side="bottom" sideOffset={6} className="rounded bg-tooltip-bg px-2.5 py-1.5 text-xs text-tooltip-fg shadow-sm animate-in fade-in-0 zoom-in-95">
                    Save all changes
                    <Tooltip.Arrow className="fill-tooltip-bg" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>

              {/* Status-dependent actions */}
              {(page.status === 'draft' || page.status === 'scheduled') && (
                <button
                  type="button"
                  onClick={() => setShowPublish(true)}
                  disabled={isBusy || isDirty}
                  className="flex items-center gap-1.5 rounded border border-green-7 bg-green-3 px-3 py-1.5 text-xs font-semibold text-green-11 transition-colors hover:bg-green-4 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Radio size={12} aria-hidden="true" />
                  Publish
                </button>
              )}

              {page.status === 'draft' && (
                <button
                  type="button"
                  onClick={() => setShowSchedule(true)}
                  disabled={isBusy || isDirty}
                  className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <CalendarClock size={12} aria-hidden="true" />
                  Schedule…
                </button>
              )}

              {page.status === 'scheduled' && (
                <button
                  type="button"
                  onClick={() => setShowReschedule(true)}
                  disabled={isBusy || isDirty}
                  className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <CalendarClock size={12} aria-hidden="true" />
                  Reschedule…
                </button>
              )}

              {(page.status === 'draft' || page.status === 'scheduled' || page.status === 'live') && (
                <button
                  type="button"
                  onClick={handleArchive}
                  disabled={isBusy}
                  className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-red-3 hover:text-red-11 hover:border-red-7 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {isArchiving
                    ? <Loader2 size={12} className="animate-spin" />
                    : <Archive size={12} aria-hidden="true" />
                  }
                  Archive
                </button>
              )}
            </div>
          )}

          {readOnly && (
            <span className="text-[11px] italic text-fg-secondary opacity-60">
              {page.status === 'live' ? 'Read-only — page is live' : 'Read-only — archived page'}
            </span>
          )}
        </div>

        {/* Read-only banner — live or archived */}
        {readOnly && (
          <div
            className="flex shrink-0 items-center gap-3 border-b px-5 py-3"
            style={{
              backgroundColor: page.status === 'live' ? 'var(--orange-2)' : 'var(--gray-2)',
              borderColor:     page.status === 'live' ? 'var(--orange-6)' : 'var(--gray-5)',
            }}
          >
            <Lock
              size={13}
              aria-hidden="true"
              className="shrink-0"
              style={{ color: page.status === 'live' ? 'var(--orange-9)' : 'var(--gray-9)' }}
            />
            <p
              className="flex-1 text-xs leading-snug"
              style={{ color: page.status === 'live' ? 'var(--orange-11)' : 'var(--gray-11)' }}
            >
              {page.status === 'live'
                ? 'This page is live and cannot be edited. To make changes, go to Pages, create a new page using "New Page", select this page as the source to clone it, make your edits, then publish the new page to push it live.'
                : 'This page is archived and cannot be edited.'}
            </p>
          </div>
        )}

        <PageCanvas
          canvas={canvas}
          contentMap={contentMap}
          onSetContent={setBlockContent}
          onRemoveBlock={removeBlockContent}
          readOnly={readOnly}
        />
      </section>

      <PageRightShelf
        canvas={canvas}
        contentMap={contentMap}
        onSetContent={setBlockContent}
        readOnly={readOnly}
        pageKind={page.kind}
        pool={pool}
        onPoolChange={(p) => { setPool(p); setIsDirty(true) }}
      />

    </div>
    </BlockEditorDndContext>
  )
}

export default PageEditor
